import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Clock, 
  Heart, 
  Shield, 
  Zap, 
  Users, 
  TrendingUp, 
  Award, 
  CheckCircle,
  Star,
  MapPin,
  Percent,
  Phone,
  CreditCard,
  Building2,
  AlertCircle
} from 'lucide-react';

const WhyDailzy = () => {
  const reasons = [
    {
      icon: Clock,
      title: "Lightning Fast Delivery",
      description: "Get your orders in 12 minutes or less. We've revolutionized quick commerce with our hyper-local delivery network.",
      stat: "12 min",
      statLabel: "Average delivery",
      color: "bg-blue-50 text-blue-600 border-blue-200",
      iconColor: "text-blue-500"
    },
    {
      icon: Heart,
      title: "Supporting Local Vendors",
      description: "Every order helps local businesses grow. We connect you with trusted neighborhood stores and vendors.",
      stat: "15K+",
      statLabel: "Local vendors",
      color: "bg-green-50 text-green-600 border-green-200",
      iconColor: "text-green-500"
    },
    {
      icon: Shield,
      title: "100% Quality Guaranteed",
      description: "Fresh products, verified vendors, and secure transactions. Your satisfaction is our top priority.",
      stat: "99.8%",
      statLabel: "Quality rating",
      color: "bg-purple-50 text-purple-600 border-purple-200",
      iconColor: "text-purple-500"
    },
    {
      icon: Percent,
      title: "Transparent Pricing",
      description: "No hidden charges, fair prices, and regular discounts. What you see is what you pay.",
      stat: "0%",
      statLabel: "Hidden fees",
      color: "bg-orange-50 text-orange-600 border-orange-200",
      iconColor: "text-orange-500"
    },
    {
      icon: Phone,
      title: "24/7 Customer Support",
      description: "Round-the-clock assistance for all your needs. Our support team is always ready to help.",
      stat: "24/7",
      statLabel: "Support available",
      color: "bg-red-50 text-red-600 border-red-200",
      iconColor: "text-red-500"
    },
    {
      icon: Star,
      title: "Highly Rated Platform",
      description: "Trusted by thousands of customers across India. Join our growing community of satisfied users.",
      stat: "4.8★",
      statLabel: "User rating",
      color: "bg-yellow-50 text-yellow-600 border-yellow-200",
      iconColor: "text-yellow-500"
    },
    {
      icon: CreditCard,
      title: "Trusted Payment Partners",
      description: "Secure payments with Google Pay, Paytm, PhonePe, Razorpay. SSL secured, PCI compliant, and ISO certified.",
      stat: "100%",
      statLabel: "Secure payments",
      color: "bg-indigo-50 text-indigo-600 border-indigo-200",
      iconColor: "text-indigo-500"
    },
    {
      icon: Building2,
      title: "Lending Partners",
      description: "Access to flexible financing options through HDFC Bank, ICICI Bank, Bajaj Finserv, and other trusted lending partners.",
      stat: "Planned",
      statLabel: "Coming soon",
      color: "bg-gray-50 text-gray-600 border-gray-200",
      iconColor: "text-gray-500",
      isPlanned: true
    }
  ];

  const comparisonPoints = [
    { feature: "Delivery Speed", dailzy: "10 minutes", others: "30-60 minutes" },
    { feature: "Local Vendor Support", dailzy: "100% Local", others: "Limited" },
    { feature: "Hidden Charges", dailzy: "₹0", others: "Multiple fees" },
    { feature: "Quality Assurance", dailzy: "Guaranteed", others: "Variable" },
    { feature: "Customer Support", dailzy: "24/7", others: "Business hours" },
    { feature: "Vendor Commission", dailzy: "Low rates", others: "High rates" }
  ];

  const achievements = [
    { number: "2.5M+", label: "Orders Delivered", icon: CheckCircle },
    { number: "50+", label: "Cities Covered", icon: MapPin },
    { number: "98%", label: "On-time Delivery", icon: Clock },
    { number: "15K+", label: "Happy Vendors", icon: Users }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-white via-gray-50 to-red-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-red-200 to-orange-200 rounded-full opacity-10 blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-orange-200 to-red-200 rounded-full opacity-10 blur-3xl translate-x-1/2 translate-y-1/2"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          {/* <motion.div 
            className="inline-flex items-center gap-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-500 rounded-2xl px-4 py-2 mb-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Award size={20} />
            <span className="font-semibold text-sm">Why Choose Us</span>
          </motion.div> */}
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
            Why Choose <span className="bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">Dailzy?</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Experience the difference with India's most trusted quick commerce platform. Here's what makes us special.
          </p>
        </motion.div>

        {/* Main reasons grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              className={`bg-white rounded-2xl p-8 border-2 ${reason.color} hover:shadow-xl transition-all duration-300 group relative overflow-hidden ${reason.isPlanned ? 'opacity-75' : ''}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
            >
              {/* Planned tag */}
              {reason.isPlanned && (
                <div className="absolute top-4 right-4 bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  PLANNED
                </div>
              )}
              
              {/* Background gradient on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <div className="relative z-10">
                {/* Icon and stat */}
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-white border-2 flex items-center justify-center ${reason.color} group-hover:scale-110 transition-transform duration-300`}>
                    <reason.icon className={`w-8 h-8 ${reason.iconColor}`} />
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${reason.iconColor}`}>{reason.stat}</div>
                    <div className="text-xs text-gray-500">{reason.statLabel}</div>
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-gray-900 transition-colors">
                  {reason.title}
                </h3>
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
                  {reason.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comparison section */}
        {/* <motion.div 
          className="mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">
              Dailzy vs <span className="text-gray-400">Others</span>
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              See how we stack up against traditional delivery platforms
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-4xl mx-auto">
            <div className="grid grid-cols-3 bg-gradient-to-r from-red-500 to-orange-500 text-white p-4">
              <div className="font-semibold">Feature</div>
              <div className="font-semibold text-center">Dailzy</div>
              <div className="font-semibold text-center opacity-80">Others</div>
            </div>
            {comparisonPoints.map((point, index) => (
              <motion.div 
                key={index}
                className="grid grid-cols-3 p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="font-medium text-gray-700">{point.feature}</div>
                <div className="text-center">
                  <span className="inline-flex items-center bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {point.dailzy}
                  </span>
                </div>
                <div className="text-center text-gray-500 text-sm">{point.others}</div>
              </motion.div>
            ))}
          </div>
        </motion.div> */}

        {/* Achievements */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-8">
            Our Achievements
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 group"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <achievement.icon className="w-8 h-8 text-red-500 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <div className="text-3xl font-bold text-gray-800 mb-2">{achievement.number}</div>
                <div className="text-sm text-gray-600">{achievement.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Call to action */}
          {/* <motion.div 
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-red-500 via-red-600 to-orange-500 rounded-2xl p-8 text-white max-w-2xl mx-auto">
              <h4 className="text-2xl font-bold mb-4">Ready to Experience the Difference?</h4>
              <p className="text-lg opacity-90 mb-6">
                Join thousands of satisfied customers and vendors who choose Dailzy every day.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="bg-white text-red-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center">
                  <Users className="w-5 h-5 mr-2" />
                  Start Shopping
                </button>
                <Link to="/vendors">
                  <button className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white/10 transition-colors flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    Join as Vendor
                  </button>
                </Link>
              </div>
            </div>
          </motion.div> */}
        </motion.div>
      </div>
    </section>
  );
};

export default WhyDailzy; 